import { PropsWithChildren } from 'react';

import { useAnnotatedPDFViewerContext } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { AnnotatedPDFViewerProvider } from '@/modules/pdf/PDFViewer/AnnotatedPDFViewer/context/AnnotatedPDFViewer.provider';

import { CitationDocumentSideContent } from '../chatSideContent.types';
import { ChatDocumentViewer } from '../documentViewer/ChatDocumentViewer';

/**
 * The pane-scoped viewer state for a cited source document. The citation
 * snapshot travels in the content descriptor and seeds a provider scoped to
 * the pane, so stepping between passages or searching inside the pane can't
 * disturb (or be disturbed by) the chat transcript beside it.
 *
 * Mounted around the whole pane chrome (not just the body) so the title can
 * follow the live active annotation as the reader steps between passages.
 */
export function CitationDocumentSideContentScope({
  content,
  children,
}: PropsWithChildren<{ content: CitationDocumentSideContent }>) {
  return (
    <AnnotatedPDFViewerProvider
      showCitations
      // The coachmark's "prev/next below the document" instructions describe
      // the document-details layout, not this pane
      showCitationCoachmark={false}
      documentId={content.documentId}
      overrideAnnotations={content.annotations}
      overrideActiveAnnotationIndex={content.activeAnnotationIndex}
    >
      {children}
    </AnnotatedPDFViewerProvider>
  );
}

/**
 * Pane title for a cited source document: the name of the document the reader
 * is looking at. Follows the live active annotation (stepping between
 * passages can cross into another document), reading the name the citation
 * resolution stamped on the annotation — no extra lookup. Falls back to a
 * generic title for annotations without one.
 */
export function CitationDocumentSideContentTitle() {
  const { annotations, activeAnnotationIndex } = useAnnotatedPDFViewerContext();
  const documentName = annotations?.[activeAnnotationIndex]?.documentName;
  return <>{documentName ?? 'Source document'}</>;
}

/**
 * Shows the cited source document with its cited passages highlighted, from
 * the pane-scoped provider mounted by CitationDocumentSideContentScope.
 */
export function CitationDocumentSideContentBody() {
  return <ChatDocumentViewer initialTab="document" />;
}
